import React from 'react';
import { Link } from 'react-router-dom';
import { AlignCenter, ChartBar, LineChart, BookOpen, ArrowRight, Star, Layers, Equalizer, TrendingUp, BookText, BrainCircuit, BarChart4, Database, Scales } from 'lucide-react';

const HomePage = () => {
  const sections = [
    {
      title: "Manager Selection",
      description: "Tools and research focused on manager selection and performance attribution",
      icon: <ChartBar className="w-6 h-6" />,
      gradientFrom: "from-blue-500",
      gradientTo: "to-cyan-500",
      categories: [
        {
          title: "Tools",
          items: [
            {
              name: "Return-Based Attribution Tool",
              description: "Analyze manager performance and attribute returns to specific factors",
              path: "/insights/manager-selection/tools/return-attribution",
              icon: <LineChart className="w-5 h-5" />,
              isNew: true
            }
          ]
        },
        {
          title: "Research",
          items: [
            {
              name: "Tracking Error Impact",
              description: "Analysis of tracking error effects on fundamental and quantitative managers",
              path: "/insights/manager-selection/research/quant-vs-fundamental",
              icon: <TrendingUp className="w-5 h-5" />
            }
          ]
        }
      ]
    },
    {
      title: "Total Portfolio Management",
      description: "Portfolio optimization and implementation tools",
      icon: <Layers className="w-6 h-6" />,
      gradientFrom: "from-purple-500",
      gradientTo: "to-pink-500",
      categories: [
        {
          title: "Tools",
          items: [
            {
              name: "Pacing Model",
              description: "Optimize portfolio rebalancing and transition management",
              path: "/insights/tpm/tools/pacing",
              icon: <BarChart4 className="w-5 h-5" />,
              isNew: true
            },
            {
              name: "Fit Cashflows to Pacing Model",
              description: "Fit cashflows to pacing model and optimize transition management",
              path: "/insights/tpm/tools/fit-to-ta",
              icon: <AlignCenter className="w-5 h-5" />,
              isNew: true
            }
          ]
        },
        {
          title: "Research",
          items: [
            {
              name: "Market Efficiency Analysis",
              description: "Impact of market efficiency on manager performance",
              path: "/insights/tpm/research/market-efficiency",
              icon: <BrainCircuit className="w-5 h-5" />
            },
            {
              name: "Fundamental-Quant Optimizer",
              description: "Public Equity: Quant vs. Fundamental Optimal Allocations",
              path: "/insights/tpm/research/fundamental-quant-optimizer",
              icon: <BarChart4 className="w-5 h-5" />
            }
          ]
        }
      ]
    },
    {
      title: "Other Projects",
      description: "Additional research and innovative projects",
      icon: <BookOpen className="w-6 h-6" />,
      gradientFrom: "from-orange-500",
      gradientTo: "to-red-500",
      categories: [
        {
          title: "Projects",
          items: [
            {
              name: "Micro-Cap Quantitative Fund",
              description: "Analysis and implementation of quantitative strategies in micro-cap equities",
              path: "/insights/other/projects/penny-ml",
              icon: <Star className="w-5 h-5" />
            }
          ]
        },
        {
          title: "Research",
          items: [
            {
              name: "eVestment Dataset Analysis",
              description: "Investigation of biases in institutional investment databases",
              path: "/insights/other/research/evestment-bias",
              icon: <Database className="w-5 h-5" />
            }
          ]
        }
      ]
    }
  ];

  const ContentCard = ({ item }) => (
    <Link
      to={item.path}
      className={`
        group relative overflow-hidden rounded-xl p-6 transition-all duration-300
        ${item.comingSoon ? 'cursor-not-allowed opacity-70' : 'hover:shadow-lg hover:-translate-y-1'}
        bg-white border border-gray-100
      `}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <div className="text-gray-600 group-hover:text-blue-600 transition-colors">
              {item.icon}
            </div>
            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
              {item.name}
            </h3>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed">
            {item.description}
          </p>
        </div>
        {!item.comingSoon && (
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors transform group-hover:translate-x-1" />
        )}
      </div>
      {item.isNew && (
        <span className="absolute top-4 right-4 text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full font-medium">
          New
        </span>
      )}
      {item.comingSoon && (
        <span className="absolute top-4 right-4 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium">
          Coming Soon
        </span>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-white border-b border-gray-100">
        <div className="max-w-screen-xl mx-auto px-4 py-16 sm:py-24">
          <div className="text-center">
            {/* <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl mb-6">
              Asset Management Tools & Research
            </h1> */}
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600">
              A collection of tools and research that help me work.
            </p>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-8 text-gray-600">
              Made by Jasper, in my free time.
            </p>
          </div>
        </div>
        <div className="absolute inset-x-0 top-0 -z-10 transform-gpu overflow-hidden blur-3xl" aria-hidden="true">
          <div className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-blue-200 to-blue-50 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]" />
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-screen-xl mx-auto px-4 py-16">
        <div className="grid gap-16">
          {sections.map((section, idx) => (
            <div key={idx}>
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg bg-gradient-to-r ${section.gradientFrom} ${section.gradientTo} text-white`}>
                    {section.icon}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{section.title}</h2>
                    <p className="text-gray-600 mt-1">{section.description}</p>
                  </div>
                </div>
              </div>

              <div className="grid gap-8">
                {section.categories.map((category, catIdx) => (
                  <div key={catIdx}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">{category.title}</h3>
                    <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
                      {category.items.map((item, itemIdx) => (
                        <ContentCard key={itemIdx} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-100">
        <div className="max-w-screen-xl mx-auto px-4 py-12">
          <div className="text-center">
            <Link 
              to="/insights/about-me"
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              <BookText className="w-5 h-5" />
              <span>Learn more about this project</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
